/** Inventory domain events — catalog 1.3.0 additions. */
import {
  asEventRecord,
  defineEvent,
  optionalNumber,
  optionalString,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface InventoryStockAdjustedPayload {
  orgId: string;
  warehouseId: string;
  itemType: InventoryItemType;
  itemId: string;
  delta: number;
  quantity: number;
  orderId?: string;
  workspaceId?: string;
}

export interface InventoryHoldEventPayload {
  orgId: string;
  warehouseId: string;
  itemType: InventoryItemType;
  itemId: string;
  holdId: string;
  quantity?: number;
  orderId?: string;
  workspaceId?: string;
}

export interface InventoryRestrictionToggledPayload {
  orgId: string;
  workspaceId: string;
  restrictionEnabled: boolean;
}

export interface InventoryStockDriftPayload {
  orgId: string;
  warehouseId: string;
  itemType: InventoryItemType;
  itemId: string;
  computedQuantity: number;
  storedQuantity: number;
}

export interface InventoryNegativeStockPayload {
  orgId: string;
  warehouseId: string;
  itemType: InventoryItemType;
  itemId: string;
  quantity: number;
  workspaceId?: string;
}

export interface InventoryWarehouseCreatedPayload {
  warehouseId: string;
  orgId: string;
  name: string;
}

export interface InventoryWarehouseUpdatedPayload {
  warehouseId: string;
  orgId: string;
  name?: string;
  status?: string;
}

export interface InventoryWarehouseDeletedPayload {
  warehouseId: string;
  orgId: string;
}

export interface InventoryWarehouseWorkspacePayload {
  orgId: string;
  workspaceId: string;
  warehouseId: string;
}

export type InventoryItemType = 'product' | 'variant' | 'bundle' | 'addon';

const itemTypes: readonly InventoryItemType[] = ['product', 'variant', 'bundle', 'addon'];

const requireItemType = (value: unknown, context: string): InventoryItemType => {
  if (typeof value !== 'string' || !itemTypes.includes(value as InventoryItemType)) {
    throw new Error(`${context}: field "itemType" must be one of ${itemTypes.join(', ')}`);
  }
  return value as InventoryItemType;
};

const stockAdjustedSchema: EventSchema<InventoryStockAdjustedPayload> = {
  parse(data) {
    const record = asEventRecord(data, 'inventory:stock-adjusted');
    const orderId = optionalString(record.orderId, 'orderId', 'inventory:stock-adjusted');
    const workspaceId = optionalString(
      record.workspaceId,
      'workspaceId',
      'inventory:stock-adjusted',
    );
    return {
      orgId: requireString(record.orgId, 'orgId', 'inventory:stock-adjusted'),
      warehouseId: requireString(record.warehouseId, 'warehouseId', 'inventory:stock-adjusted'),
      itemType: requireItemType(record.itemType, 'inventory:stock-adjusted'),
      itemId: requireString(record.itemId, 'itemId', 'inventory:stock-adjusted'),
      delta: requireNumber(record.delta, 'delta', 'inventory:stock-adjusted'),
      quantity: requireNumber(record.quantity, 'quantity', 'inventory:stock-adjusted'),
      ...(orderId === undefined ? {} : { orderId }),
      ...(workspaceId === undefined ? {} : { workspaceId }),
    };
  },
};

const holdSchema = (type: string): EventSchema<InventoryHoldEventPayload> => ({
  parse(data) {
    const record = asEventRecord(data, type);
    const quantity = optionalNumber(record.quantity, 'quantity', type);
    const orderId = optionalString(record.orderId, 'orderId', type);
    const workspaceId = optionalString(record.workspaceId, 'workspaceId', type);
    return {
      orgId: requireString(record.orgId, 'orgId', type),
      warehouseId: requireString(record.warehouseId, 'warehouseId', type),
      itemType: requireItemType(record.itemType, type),
      itemId: requireString(record.itemId, 'itemId', type),
      holdId: requireString(record.holdId, 'holdId', type),
      ...(quantity === undefined ? {} : { quantity }),
      ...(orderId === undefined ? {} : { orderId }),
      ...(workspaceId === undefined ? {} : { workspaceId }),
    };
  },
});

const restrictionToggledSchema: EventSchema<InventoryRestrictionToggledPayload> = {
  parse(data) {
    const record = asEventRecord(data, 'inventory:restriction-toggled');
    if (typeof record.restrictionEnabled !== 'boolean') {
      throw new Error(
        'inventory:restriction-toggled: field "restrictionEnabled" must be a boolean',
      );
    }
    return {
      orgId: requireString(record.orgId, 'orgId', 'inventory:restriction-toggled'),
      workspaceId: requireString(
        record.workspaceId,
        'workspaceId',
        'inventory:restriction-toggled',
      ),
      restrictionEnabled: record.restrictionEnabled,
    };
  },
};

const stockDriftSchema: EventSchema<InventoryStockDriftPayload> = {
  parse(data) {
    const record = asEventRecord(data, 'inventory:stock-drift-detected');
    return {
      orgId: requireString(record.orgId, 'orgId', 'inventory:stock-drift-detected'),
      warehouseId: requireString(
        record.warehouseId,
        'warehouseId',
        'inventory:stock-drift-detected',
      ),
      itemType: requireItemType(record.itemType, 'inventory:stock-drift-detected'),
      itemId: requireString(record.itemId, 'itemId', 'inventory:stock-drift-detected'),
      computedQuantity: requireNumber(
        record.computedQuantity,
        'computedQuantity',
        'inventory:stock-drift-detected',
      ),
      storedQuantity: requireNumber(
        record.storedQuantity,
        'storedQuantity',
        'inventory:stock-drift-detected',
      ),
    };
  },
};

const negativeStockSchema: EventSchema<InventoryNegativeStockPayload> = {
  parse(data) {
    const record = asEventRecord(data, 'inventory:negative-stock-flagged');
    const workspaceId = optionalString(
      record.workspaceId,
      'workspaceId',
      'inventory:negative-stock-flagged',
    );
    return {
      orgId: requireString(record.orgId, 'orgId', 'inventory:negative-stock-flagged'),
      warehouseId: requireString(
        record.warehouseId,
        'warehouseId',
        'inventory:negative-stock-flagged',
      ),
      itemType: requireItemType(record.itemType, 'inventory:negative-stock-flagged'),
      itemId: requireString(record.itemId, 'itemId', 'inventory:negative-stock-flagged'),
      quantity: requireNumber(record.quantity, 'quantity', 'inventory:negative-stock-flagged'),
      ...(workspaceId === undefined ? {} : { workspaceId }),
    };
  },
};

const warehouseCreatedSchema: EventSchema<InventoryWarehouseCreatedPayload> = {
  parse(data) {
    const record = asEventRecord(data, 'inventory:warehouse-created');
    return {
      warehouseId: requireString(record.warehouseId, 'warehouseId', 'inventory:warehouse-created'),
      orgId: requireString(record.orgId, 'orgId', 'inventory:warehouse-created'),
      name: requireWarehouseString(record.name, 'name', 'inventory:warehouse-created'),
    };
  },
};

const warehouseUpdatedSchema: EventSchema<InventoryWarehouseUpdatedPayload> = {
  parse(data) {
    const record = asEventRecord(data, 'inventory:warehouse-updated');
    const name = optionalWarehouseString(record.name, 'name', 'inventory:warehouse-updated');
    const status = optionalWarehouseString(record.status, 'status', 'inventory:warehouse-updated');
    return {
      warehouseId: requireString(record.warehouseId, 'warehouseId', 'inventory:warehouse-updated'),
      orgId: requireString(record.orgId, 'orgId', 'inventory:warehouse-updated'),
      ...(name === undefined ? {} : { name }),
      ...(status === undefined ? {} : { status }),
    };
  },
};

function requireWarehouseString(value: unknown, field: string, context: string): string {
  if (typeof value !== 'string') {
    throw new Error(`${context}: field "${field}" must be a string`);
  }
  return value;
}

function optionalWarehouseString(
  value: unknown,
  field: string,
  context: string,
): string | undefined {
  return value === undefined ? undefined : requireWarehouseString(value, field, context);
}

const warehouseDeletedSchema: EventSchema<InventoryWarehouseDeletedPayload> = {
  parse(data) {
    const record = asEventRecord(data, 'inventory:warehouse-deleted');
    return {
      warehouseId: requireString(record.warehouseId, 'warehouseId', 'inventory:warehouse-deleted'),
      orgId: requireString(record.orgId, 'orgId', 'inventory:warehouse-deleted'),
    };
  },
};

const warehouseWorkspaceSchema = (
  type: 'inventory:warehouse-attached' | 'inventory:warehouse-detached',
): EventSchema<InventoryWarehouseWorkspacePayload> => ({
  parse(data) {
    const record = asEventRecord(data, type);
    return {
      orgId: requireString(record.orgId, 'orgId', type),
      workspaceId: requireString(record.workspaceId, 'workspaceId', type),
      warehouseId: requireString(record.warehouseId, 'warehouseId', type),
    };
  },
});

function requireNumber(value: unknown, field: string, context: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${context}: field "${field}" must be a finite number`);
  }
  return value;
}

const defineHoldEvent = (
  type:
    | 'inventory:hold-placed'
    | 'inventory:hold-consumed'
    | 'inventory:hold-released'
    | 'inventory:hold-expired',
  description: string,
): EventDefinition<InventoryHoldEventPayload> =>
  defineEvent<InventoryHoldEventPayload>({
    type,
    version: 1,
    schema: holdSchema(type),
    auditable: true,
    description,
    since: '1.3.0',
  });

export const INVENTORY_STOCK_ADJUSTED_EVENT: EventDefinition<InventoryStockAdjustedPayload> =
  defineEvent({
    type: 'inventory:stock-adjusted',
    version: 1,
    schema: stockAdjustedSchema,
    auditable: true,
    description: 'Stock quantity was adjusted and recorded as a movement.',
    since: '1.3.0',
  });

export const INVENTORY_HOLD_PLACED_EVENT = defineHoldEvent(
  'inventory:hold-placed',
  'A stock hold was placed.',
);
export const INVENTORY_HOLD_CONSUMED_EVENT = defineHoldEvent(
  'inventory:hold-consumed',
  'A stock hold was consumed.',
);
export const INVENTORY_HOLD_RELEASED_EVENT = defineHoldEvent(
  'inventory:hold-released',
  'A stock hold was released.',
);
export const INVENTORY_HOLD_EXPIRED_EVENT = defineHoldEvent(
  'inventory:hold-expired',
  'A stock hold expired.',
);

export const INVENTORY_RESTRICTION_TOGGLED_EVENT: EventDefinition<InventoryRestrictionToggledPayload> =
  defineEvent({
    type: 'inventory:restriction-toggled',
    version: 1,
    schema: restrictionToggledSchema,
    auditable: true,
    description: 'Workspace inventory restrictions were enabled or disabled.',
    since: '1.3.0',
  });

export const INVENTORY_STOCK_DRIFT_DETECTED_EVENT: EventDefinition<InventoryStockDriftPayload> =
  defineEvent({
    type: 'inventory:stock-drift-detected',
    version: 1,
    schema: stockDriftSchema,
    auditable: true,
    description: 'A stock ledger quantity differs from its stored projection.',
    since: '1.3.0',
  });

export const INVENTORY_NEGATIVE_STOCK_FLAGGED_EVENT: EventDefinition<InventoryNegativeStockPayload> =
  defineEvent({
    type: 'inventory:negative-stock-flagged',
    version: 1,
    schema: negativeStockSchema,
    auditable: true,
    description: 'Stock quantity fell below zero and was flagged for review.',
    since: '1.3.0',
  });

export const INVENTORY_WAREHOUSE_CREATED_EVENT: EventDefinition<InventoryWarehouseCreatedPayload> =
  defineEvent({
    type: 'inventory:warehouse-created',
    version: 1,
    schema: warehouseCreatedSchema,
    auditable: true,
    description: 'An inventory warehouse was created.',
    since: '1.3.0',
  });

export const INVENTORY_WAREHOUSE_UPDATED_EVENT: EventDefinition<InventoryWarehouseUpdatedPayload> =
  defineEvent({
    type: 'inventory:warehouse-updated',
    version: 1,
    schema: warehouseUpdatedSchema,
    auditable: true,
    description: 'An inventory warehouse was updated.',
    since: '1.3.0',
  });

export const INVENTORY_WAREHOUSE_DELETED_EVENT: EventDefinition<InventoryWarehouseDeletedPayload> =
  defineEvent({
    type: 'inventory:warehouse-deleted',
    version: 1,
    schema: warehouseDeletedSchema,
    auditable: true,
    description: 'An inventory warehouse was deleted.',
    since: '1.3.0',
  });

export const INVENTORY_WAREHOUSE_ATTACHED_EVENT: EventDefinition<InventoryWarehouseWorkspacePayload> =
  defineEvent({
    type: 'inventory:warehouse-attached',
    version: 1,
    schema: warehouseWorkspaceSchema('inventory:warehouse-attached'),
    auditable: true,
    description: 'A warehouse was attached to a workspace.',
    since: '1.3.0',
  });

export const INVENTORY_WAREHOUSE_DETACHED_EVENT: EventDefinition<InventoryWarehouseWorkspacePayload> =
  defineEvent({
    type: 'inventory:warehouse-detached',
    version: 1,
    schema: warehouseWorkspaceSchema('inventory:warehouse-detached'),
    auditable: true,
    description: 'A warehouse was detached from a workspace.',
    since: '1.3.0',
  });

export const INVENTORY_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [INVENTORY_STOCK_ADJUSTED_EVENT.type]: INVENTORY_STOCK_ADJUSTED_EVENT,
  [INVENTORY_HOLD_PLACED_EVENT.type]: INVENTORY_HOLD_PLACED_EVENT,
  [INVENTORY_HOLD_CONSUMED_EVENT.type]: INVENTORY_HOLD_CONSUMED_EVENT,
  [INVENTORY_HOLD_RELEASED_EVENT.type]: INVENTORY_HOLD_RELEASED_EVENT,
  [INVENTORY_HOLD_EXPIRED_EVENT.type]: INVENTORY_HOLD_EXPIRED_EVENT,
  [INVENTORY_RESTRICTION_TOGGLED_EVENT.type]: INVENTORY_RESTRICTION_TOGGLED_EVENT,
  [INVENTORY_STOCK_DRIFT_DETECTED_EVENT.type]: INVENTORY_STOCK_DRIFT_DETECTED_EVENT,
  [INVENTORY_NEGATIVE_STOCK_FLAGGED_EVENT.type]: INVENTORY_NEGATIVE_STOCK_FLAGGED_EVENT,
  [INVENTORY_WAREHOUSE_CREATED_EVENT.type]: INVENTORY_WAREHOUSE_CREATED_EVENT,
  [INVENTORY_WAREHOUSE_UPDATED_EVENT.type]: INVENTORY_WAREHOUSE_UPDATED_EVENT,
  [INVENTORY_WAREHOUSE_DELETED_EVENT.type]: INVENTORY_WAREHOUSE_DELETED_EVENT,
  [INVENTORY_WAREHOUSE_ATTACHED_EVENT.type]: INVENTORY_WAREHOUSE_ATTACHED_EVENT,
  [INVENTORY_WAREHOUSE_DETACHED_EVENT.type]: INVENTORY_WAREHOUSE_DETACHED_EVENT,
};
